/**
 * @file routes/_app/risk-assessment/market-sampling.tsx
 * @description 市售抽樣查驗統計報表（客戶 PPT S25 第 2 部分 / S27）。
 *
 * 系統自動統計該年度各驗證方案的市售抽樣（年度後市場採樣）比例，分四個分類：
 * 有機(農糧) / 有機(加工分流) / 產銷履歷(農糧) / 產銷履歷(加工分流)。
 * 紅線：每分類市售抽樣件數須達「該類總數」（當年度已發證且有效案件之去重客戶數）的 3% 以上。
 *
 * 頁面結構：
 * - 年度選擇 + 四分類統計卡（件數/總數/比例/達標 Tag）
 * - 明細表（同一數據的表格視圖，便於列印/核對）
 * - 口徑說明（3% 紅線 + 後端回傳 note 原文）
 *
 * 權限：讀 analytics.read（admin / manager / decider）——與其他統計頁一致，後端強制。
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { Alert, Button, Col, Row, Select, Spin, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, Download, FlaskConical, PenLine, ShieldCheck, Table2 } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { message } from '@/lib/antd-app';
import { useAuthStore } from '@/lib/auth-context';
import { PageShell, PageMetaItem, SectionCard, StatCard } from '@/components/shell';
import { userCan, type MarketSamplingCategoryStat, type MarketSamplingReport } from '@zhcb/shared';
import { color, space } from '@/styles/tokens';

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/risk-assessment/market-sampling')({
  component: MarketSamplingPage,
});

/** 比例格式化：0.031 → 3.1%（保留 1 位小數） */
function formatRatio(ratio: number): string {
  return `${(ratio * 100).toFixed(1)}%`;
}

/** 達標 Tag：達標綠 / 未達標紅 / 無母數灰 */
function MetTag({ stat }: { stat: MarketSamplingCategoryStat }) {
  if (stat.totalCount === 0) return <Tag>無母數</Tag>;
  return stat.met ? <Tag color="green">達標</Tag> : <Tag color="red">未達標</Tag>;
}

function MarketSamplingPage() {
  /* ---- 年度選擇（近 6 年） ---- */
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(currentYear);
  const user = useAuthStore((state) => state.user);
  const queryClient = useQueryClient();
  const canSign = !!user && userCan(user, 'market-sampling.sign');
  const canExport = !!user && userCan(user, 'analytics.export');
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i).map((y) => ({
    value: y,
    label: `${y} 年度`,
  }));

  /* ---- 報表查詢 ---- */
  const reportQuery = useQuery({
    queryKey: ['market-sampling', year],
    queryFn: () =>
      apiClient.get<MarketSamplingReport>(`/api/analytics/market-sampling?year=${year}`),
  });
  const report = reportQuery.data;
  const categories = report?.categories ?? [];
  const unmetCount = categories.filter((c) => !c.met).length;
  const totalSamples = categories.reduce((acc, c) => acc + c.sampleCount, 0);

  const signMutation = useMutation({
    mutationFn: async () => {
      const signature = await apiClient.get<{ fileId: number | null }>('/api/auth/me/signature');
      return apiClient.post(`/api/analytics/market-sampling/sign?year=${year}`, {
        signatureFileId: signature.fileId ?? undefined,
      });
    },
    onSuccess: () => {
      message.success('年度市售抽樣報表已簽核');
      void queryClient.invalidateQueries({ queryKey: ['market-sampling', year] });
    },
    onError: (error) => {
      console.error('sign market sampling report failed', error);
      message.error(error instanceof Error ? error.message : '簽核失敗');
    },
  });

  /* ---- 明細表列定義 ---- */
  const columns: ColumnsType<MarketSamplingCategoryStat> = [
    { title: '分類', dataIndex: 'label', key: 'label' },
    {
      title: '最低應抽',
      dataIndex: 'requiredCount',
      key: 'requiredCount',
      align: 'right',
      render: (value: number) => `${value} 家`,
    },
    {
      title: '尚缺',
      dataIndex: 'shortfall',
      key: 'shortfall',
      align: 'right',
      render: (value: number) => (
        <span style={{ color: value > 0 ? color.state.danger : undefined }}>{value} 家</span>
      ),
    },
    {
      title: '市售抽樣件數',
      dataIndex: 'sampleCount',
      key: 'sampleCount',
      align: 'right',
      render: (v: number) => `${v} 件`,
    },
    {
      title: '該類總數（有效獲證客戶）',
      dataIndex: 'totalCount',
      key: 'totalCount',
      align: 'right',
      render: (v: number) => `${v} 家`,
    },
    {
      title: '抽樣比例',
      dataIndex: 'ratio',
      key: 'ratio',
      align: 'right',
      render: (_: number, row) => (
        <span
          style={{
            fontWeight: 600,
            color:
              row.totalCount === 0 ? undefined : row.met ? color.state.success : color.state.danger,
          }}
        >
          {row.totalCount === 0 ? '—' : formatRatio(row.ratio)}
        </span>
      ),
    },
    {
      title: '紅線',
      dataIndex: 'target',
      key: 'target',
      align: 'right',
      render: (v: number) => `≥ ${formatRatio(v)}`,
    },
    {
      title: '是否達標',
      key: 'met',
      align: 'center',
      render: (_, row) => <MetTag stat={row} />,
    },
  ];

  /* ---- 渲染 ---- */
  return (
    <PageShell
      title="市售抽樣查驗統計"
      description="年度市售抽樣（後市場採樣）比例統計：各分類按有效獲證客戶总数 × 3% 向上取整，有母數時至少抽 1 家。"
      actions={
        <div style={{ display: 'flex', gap: space[2], alignItems: 'center' }}>
          <Select
            value={year}
            onChange={setYear}
            options={yearOptions}
            style={{ width: 120 }}
            aria-label="統計年度"
          />
          <Link to="/risk-assessment">
            <Button icon={<ClipboardList size={14} />}>風險矩陣概觀</Button>
          </Link>
          <Link to="/risk-assessment/annual">
            <Button icon={<Table2 size={14} />}>年度評估表（附表4）</Button>
          </Link>
          {canSign && (
            <Button
              icon={<PenLine size={14} />}
              loading={signMutation.isPending}
              onClick={() => signMutation.mutate()}
            >
              {report?.signoff ? '重新簽核' : '組長簽核'}
            </Button>
          )}
          {canExport && (
            <Button
              icon={<Download size={14} />}
              onClick={() =>
                window.open(`/api/analytics/market-sampling/export.xlsx?year=${year}`, '_blank')
              }
            >
              匯出報表
            </Button>
          )}
        </div>
      }
      meta={
        <>
          <PageMetaItem
            icon={<FlaskConical size={13} />}
            label="年度市售抽樣"
            value={`${totalSamples} 件`}
          />
          <PageMetaItem
            icon={<ShieldCheck size={13} />}
            label="未達標分類"
            value={
              <span style={{ color: unmetCount > 0 ? color.state.danger : undefined }}>
                {unmetCount} 類
              </span>
            }
          />
        </>
      }
    >
      {reportQuery.isError && (
        <Alert
          type="error"
          showIcon
          message="讀取市售抽樣統計失敗"
          description={reportQuery.error?.message}
          style={{ marginBottom: space[4] }}
        />
      )}

      <Spin spinning={reportQuery.isLoading}>
        {/* 四分類統計卡 */}
        <Row gutter={[16, 16]} style={{ marginBottom: space[4] }}>
          {categories.map((cat) => (
            <Col key={cat.key} xs={24} sm={12} xl={6}>
              <StatCard
                title={cat.label}
                value={cat.totalCount === 0 ? '—' : formatRatio(cat.ratio)}
                icon={<FlaskConical size={16} />}
                tone={cat.totalCount === 0 ? 'neutral' : cat.met ? 'success' : 'danger'}
                footer={
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span>
                      {cat.sampleCount} 件 / {cat.totalCount} 家
                    </span>
                    <MetTag stat={cat} />
                  </div>
                }
              />
            </Col>
          ))}
        </Row>

        {/* 明細表 */}
        <SectionCard title={`${year} 年度市售抽樣達標明細`}>
          <Table<MarketSamplingCategoryStat>
            rowKey="key"
            columns={columns}
            dataSource={categories}
            pagination={false}
            size="middle"
          />
        </SectionCard>

        <Alert
          type={report?.signoff ? 'success' : 'warning'}
          showIcon
          style={{ marginTop: space[4] }}
          message={
            report?.signoff
              ? `驗證組組長已簽核：${report.signoff.signerName}（${report.signoff.signedAt.slice(0, 10)}）`
              : '本年度報表尚未經驗證組組長簽核'
          }
        />

        {/* 口徑說明（3% 紅線 + 後端 note 原文） */}
        <Alert
          type="info"
          showIcon
          style={{ marginTop: space[4] }}
          message="統計口徑"
          description={
            report?.note ??
            '每分類市售抽樣件數須達該類總數（當年度已發證且有效案件之去重客戶數）的 3% 以上。'
          }
        />
      </Spin>
    </PageShell>
  );
}
